import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { CopilotContext } from '@Sage/ssm-web5';

import { COPILOT_BAR, COPILOT_BAR_LIVE } from 'constants/Features';
import { getActiveCrudName } from 'utils/crud';

import { getScenarioDetectors } from './scenarios';

/**
 * Shared scenario detection for CopilotBar (wrapper + header trigger).
 */
export default function useCopilotBarScenarioMatch() {
    const location = useLocation();
    const { featureMethods } = useContext(CopilotContext);

    const entityModalState = useSelector((state) => state.entityModal || {});
    const entityCrud = useSelector((state) => state.entityCrud || {});
    const activeCrudName = useSelector((state) => getActiveCrudName(state));
    const entityCrudIsOpen = useSelector((state) => state.entityCrud?.isOpen || false);
    const isCopilotBarDashboardsEnabled = useSelector(
        (state) => state.config.flagsmith?.[COPILOT_BAR] || false,
    );
    const isCopilotBarCrudEnabled = useSelector(
        (state) => state.config.flagsmith?.[COPILOT_BAR_LIVE] || false,
    );
    const userData = useSelector((state) => state.config?.userData || {});

    const appState = useMemo(
        () => ({
            entityCrudState: {
                activeCrudName,
                entityCrud,
                isOpen: entityCrudIsOpen,
            },
            entityModalState,
            featureMethods,
            pathname: location.pathname,
            userData,
        }),
        [
            activeCrudName,
            entityCrud,
            entityCrudIsOpen,
            entityModalState,
            featureMethods,
            location.pathname,
            userData,
        ],
    );

    const matchingScenario = useMemo(() => {
        if (!isCopilotBarDashboardsEnabled && !isCopilotBarCrudEnabled) return null;

        return (
            getScenarioDetectors().find(
                ({ condition, featureFlag }) =>
                    ((featureFlag === COPILOT_BAR && isCopilotBarDashboardsEnabled) ||
                        (featureFlag === COPILOT_BAR_LIVE && isCopilotBarCrudEnabled)) &&
                    condition(appState),
            ) || null
        );
    }, [appState, isCopilotBarCrudEnabled, isCopilotBarDashboardsEnabled]);

    return {
        appState,
        isCopilotBarEnabled: isCopilotBarDashboardsEnabled || isCopilotBarCrudEnabled,
        matchingScenario,
    };
}
